import { useSyncExternalStore } from 'react';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT } from '@/services/apiService';
import { supportService } from '@/features/support/services/supportService';

/** Fire after viewing a ticket so badges refresh without waiting for the next poll. */
export const SUPPORT_UNREAD_CHANGED_EVENT = 'support-unread-changed';

const POLL_MS = 60_000;

// One poll for every badge on screen (the Help tab in the header and the bottom nav): the count
// lives in a small module store, and polling runs only while at least one component is subscribed.
let count = 0;
const listeners = new Set<() => void>();
let stopPolling: (() => void) | null = null;

function setCount(next: number) {
  if (next === count) return;
  count = next;
  listeners.forEach((listener) => listener());
}

function startPolling(): () => void {
  let cancelled = false;
  const refresh = async () => {
    if (!apiService.isAuthenticated()) {
      setCount(0);
      return;
    }
    if (document.hidden) return;
    const res = await supportService.getMyTickets(1, 20);
    if (!cancelled && res.success && res.data) {
      // The server counts unread tickets across all pages; older responses only let us count this page.
      const { unreadCount } = res.data.pagination ?? {};
      setCount(typeof unreadCount === 'number' ? unreadCount : res.data.tickets.filter((t) => t.hasUnread).length);
    }
  };
  void refresh();
  const id = window.setInterval(refresh, POLL_MS);
  const onChange = () => {
    window.setTimeout(refresh, 800);
  };
  const onAuth = () => {
    void refresh();
  };
  window.addEventListener(SUPPORT_UNREAD_CHANGED_EVENT, onChange);
  window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuth);
  document.addEventListener('visibilitychange', refresh);
  return () => {
    cancelled = true;
    window.clearInterval(id);
    window.removeEventListener(SUPPORT_UNREAD_CHANGED_EVENT, onChange);
    window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuth);
    document.removeEventListener('visibilitychange', refresh);
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!stopPolling) stopPolling = startPolling();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && stopPolling) {
      stopPolling();
      stopPolling = null;
    }
  };
}

const getSnapshot = () => count;

/**
 * Number of the signed-in player's tickets with a support reply they have not opened yet.
 * Polls once a minute while the tab is visible; 0 for guests and after sign-out.
 */
export function useSupportUnread(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Test helper — reset the module store between Jest cases. */
export function __resetSupportUnreadForTests(): void {
  if (stopPolling) stopPolling();
  stopPolling = null;
  listeners.clear();
  count = 0;
}
